// Import Libraries
import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { isPostalCodeLocal } from '../../_utils/validations';
import DeliveryOptionsForm from '../../_utils/_delivery-options/delivery-options-form';
import TaxesAndDeliveryPrompt from '../taxes-and-delivery-prompt';
import CheckoutButtons from './checkout-buttons';
import { checkoutBlockers } from '../checkout-blockers';
import UpsellAmount from '../upsell-amount';
import BackToStartButton from '../back-to-start-button';
import deliveryOptionType from '../types/delivery-option-type';
import { SubTotal } from './subtotal';
import { AlbumsMessage } from './albums-message';
import { PICKUP_DELIVERY_OPTION_TYPES } from '../../../constants/delivery-option-types';

class Full extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checkoutDisabled: true,
    };
  }

  componentDidMount() {
    this.prevZipCode = this.context.zipCode;
    this.prevPopoverOpen = this.context.popoverOpen;
    this.updateCheckoutDisabled();
  }

  componentDidUpdate(prevProps) {
    const prevZipCode = this.prevZipCode;
    const prevPopoverOpen = this.prevPopoverOpen;

    const zipCodeChanged = this.context.zipCode !== prevZipCode;
    const popoverOpenChanged = this.context.popoverOpen !== prevPopoverOpen;
    const checkoutBlockerChanged =
      prevProps.checkoutBlocker !== this.props.checkoutBlocker;

    if (zipCodeChanged || popoverOpenChanged || checkoutBlockerChanged) {
      this.updateCheckoutDisabled();
    }

    this.prevZipCode = this.context.zipCode;
    this.prevPopoverOpen = this.context.popoverOpen;
  }

  isCheckoutDisabled = () => {
    const { checkoutBlocker, selectedDeliveryOptions } = this.props;
    const { popoverOpen, zipCode, selectedLocation } = this.context;

    if (checkoutBlocker !== checkoutBlockers.none) return true;
    if (popoverOpen) return true;

    const skipZipValidation =
      selectedDeliveryOptions?.every((opt) =>
        PICKUP_DELIVERY_OPTION_TYPES.includes(opt.type),
      ) && selectedLocation;

    // Allow checkout without zipcode if only pickup/collect locations are selected
    return !skipZipValidation && !isPostalCodeLocal(zipCode);
  };

  updateCheckoutDisabled = () => {
    const newValue = this.isCheckoutDisabled();
    if (newValue !== this.state.checkoutDisabled) {
      this.setState({ checkoutDisabled: newValue });
    }
  };

  subTotal = () => {
    const { price, shippingCost } = this.props;
    return price + shippingCost;
  };

  renderDeliveryOptionsForm = () => {
    const { deliveryOptions, selectedDeliveryOptions } = this.props;

    return (
      <div data-test-id="delivery-options-form" className="d-none d-md-block">
        <p className="lead total-name">Delivery Option</p>
        <DeliveryOptionsForm
          hideIcon
          options={deliveryOptions}
          selectedOption={get(selectedDeliveryOptions, [0, 'key'])}
        />
      </div>
    );
  };

  render() {
    const { uploadedToAlbumPath, checkout, loggedIn } = this.context;
    const {
      quantity,
      checkoutBlocker,
      deliveryOptions,
      freeShippingEnabled,
      freeShippingAssetUrl,
      freeShippingThreshold,
      homeDeliveryItemsPrice,
      crossSellItemCheckOutClick,
    } = this.props;
    const { checkoutDisabled } = this.state;

    return (
      <>
        {freeShippingEnabled && (
          <UpsellAmount
            price={homeDeliveryItemsPrice}
            freeShippingThreshold={freeShippingThreshold}
            assetUrl={freeShippingAssetUrl}
            className="d-flex d-md-none"
          />
        )}
        <SubTotal
          className="d-flex d-md-none"
          quantity={quantity}
          subtotal={this.subTotal()}
        />
        {this.props.promotionMessage && (
          <div className="d-md-none mb-1">{this.props.promotionMessage}</div>
        )}
        <CheckoutButtons
          checkout={checkout}
          loggedIn={loggedIn}
          quantity={quantity}
          checkoutBlocker={checkoutBlocker}
          crossSellItemCheckOutClick={crossSellItemCheckOutClick}
          checkoutDisabled={checkoutDisabled}
          handleCheckoutClick={this.handleCheckoutClick}
        />
        <BackToStartButton />
        {freeShippingEnabled && (
          <UpsellAmount
            price={homeDeliveryItemsPrice}
            freeShippingThreshold={freeShippingThreshold}
            assetUrl={freeShippingAssetUrl}
            className="d-none d-md-flex"
          />
        )}
        {deliveryOptions && this.renderDeliveryOptionsForm()}
        <hr className="d-none d-md-block" />

        {uploadedToAlbumPath && (
          <>
            <AlbumsMessage
              uploadedToAlbumPath={uploadedToAlbumPath}
              className="d-none d-md-block"
            />
            <hr className="d-none d-md-block" />
          </>
        )}
        <SubTotal
          className="d-none d-md-flex"
          quantity={quantity}
          subtotal={this.subTotal()}
        />
        <div className="d-none d-md-block">
          <p>
            <TaxesAndDeliveryPrompt
              us="Check out to see shipping, taxes & fees"
              row="Please check out to see delivery costs"
            />
          </p>
          {this.props.promotionMessage}
        </div>
      </>
    );
  }
}

Full.propTypes = {
  checkoutBlocker: PropTypes.number,
  crossSellItemCheckOutClick: PropTypes.func,
  price: PropTypes.number,
  quantity: PropTypes.number,
  shippingCost: PropTypes.number,
  freeShippingEnabled: PropTypes.bool,
  freeShippingAssetUrl: PropTypes.string,
  freeShippingThreshold: PropTypes.number,
  homeDeliveryItemsPrice: PropTypes.number,
  deliveryOptions: PropTypes.arrayOf(deliveryOptionType),
  selectedDeliveryOptions: PropTypes.arrayOf(deliveryOptionType),
  promotionMessage: PropTypes.node,
};

Full.defaultProps = {
  checkoutBlocker: null,
  crossSellItemCheckOutClick: null,
  price: null,
  quantity: null,
  shippingCost: null,
};

Full.contextTypes = {
  zipCode: PropTypes.string,
  uploadedToAlbumPath: PropTypes.string,
  popoverOpen: PropTypes.bool,
  showPopover: PropTypes.func,
  hidePopover: PropTypes.func,
  loggedIn: PropTypes.bool,
  selectedLocation: PropTypes.string,
  checkout: PropTypes.shape({
    withoutLogin: PropTypes.bool,
    allowAccountSignupInCheckout: PropTypes.bool,
  }),
};

export default Full;
